import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { compose } from 'redux';

import { TrackEventContext } from 'common/containers/EventContainer';
import LandingIdeaManagementHelmet from 'common/helmets/LandingIdeaManagementHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import withContexts from 'common/util/withContexts';
import FAQs from 'common/www/Faqs';
import LandingCTAButtons from 'common/www/LandingCTAButtons';
import LandingFeaturesCTA from 'common/www/LandingFeaturesCTA';
import { Quote, QuotePerson, Quotes } from 'common/www/Quotes';
import Point4 from 'img/landing/feature-request-tracking-1.webp';
import Ida from 'img/landing/ida-small.webp';
import Point1 from 'img/landing/idea-management-1.webp';
import Point2 from 'img/landing/idea-management-2.webp';
import Point3 from 'img/landing/idea-management-3.webp';
import Lia from 'img/landing/lia-small.webp';
import Marcus from 'img/landing/marcus.webp';

import 'css/components/www/_LandingSolutionsPage.scss';

class LandingIdeaManagement extends Component {
  static propTypes = {
    trackEvent: PropTypes.func,
  };

  componentDidMount() {
    const { trackEvent } = this.props;
    trackEvent('Viewed Idea Management Page');
  }

  render() {
    return (
      <div className="landingIdeaManagement landingSolutionsPage">
        <LandingIdeaManagementHelmet />
        <div className="fullWidthContainer">
          <div className="header">
            <div className="left">
              <div className="text">
                <h1>Idea management software helps you make the most of your&nbsp;ideas</h1>
                <p className="subheading">
                  Streamline your idea management process, extract insights, and build
                  better&nbsp;products.
                </p>
                <LandingCTAButtons renderDemoButton />
              </div>
            </div>
            <div className="headerVideo">
              <iframe
                width="560"
                height="315"
                src="https://www.youtube.com/embed/qIncgzv9t2E?si=HxybF49F-hE7ALjN"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture;"
                allowFullScreen
              />
            </div>
          </div>
        </div>
        <div className="mainContentContainer">
          <h2>Capture, organize, and collaborate on the most valuable&nbsp;ideas</h2>
          <div className="point">
            <LazyLoadedImage
              loading="eager"
              src={Point1}
              alt="An idea board in Canny that helps collect ideas from teammates and customers"
            />
            <div className="text">
              <h2 className="title">One place for all&nbsp;ideas</h2>
              <p>
                Product ideas come in through many channels from both customers and teammates. Get
                valuable insights by gathering all ideas in one idea management&nbsp;tool.
              </p>
              <ul>
                <li>Manage idea collection at&nbsp;scale</li>
                <li>Turn customer feedback into new product&nbsp;ideas</li>
                <li>Capture employee ideas with an internal idea&nbsp;board</li>
                <li>Use idea ranking to prioritize your most important&nbsp;ideas</li>
              </ul>
            </div>
          </div>
          <div className="point">
            <LazyLoadedImage
              src={Point2}
              alt="Assigning specific types of ideas to to the relevant person in the organization"
            />
            <div className="text">
              <h2 className="title">Triage ideas to the right&nbsp;person</h2>
              <p>
                Product managers own different areas of the product. Make sure the ideas they see
                are relevant to their product&nbsp;area.
              </p>
              <ul>
                <li>
                  Make it easy for product managers to collaborate with customers and
                  develop&nbsp;ideas
                </li>
                <li>Use tags and segments to let your team see ideas that matter to&nbsp;them</li>
                <li>Empower product teams with data so they build the right&nbsp;features</li>
                <li>Connect your project management tool for seamless task&nbsp;management</li>
              </ul>
            </div>
          </div>
          <div className="point">
            <LazyLoadedImage
              src={Point3}
              alt="Idea status updates with product notes keeps stakeholders in the loop"
            />
            <div className="text">
              <h2 className="title">Manage expectations across the&nbsp;board</h2>
              <p>
                Not every feature should be built. Communicate to customers and teammates which
                features are top&nbsp;priority.
              </p>
              <ul>
                <li>Explain product&nbsp;decisions</li>
                <li>Maintain a public roadmap to show which ideas you're working&nbsp;on</li>
                <li>Automatically send alerts to keep everyone&nbsp;updated</li>
                <li>Encourage collaboration on prioritized&nbsp;features</li>
              </ul>
            </div>
          </div>
          <div className="point">
            <LazyLoadedImage
              src={Point4}
              alt="Collect ideas and votes from all your stakeholders"
            />
            <div className="text">
              <h2 className="title">Come up with innovative ideas&nbsp;together</h2>
              <p>
                Our idea management system lets stakeholders share, vote on, and discuss new ideas.
                Collaboration fosters innovation — engage with your community to fuel your
                ideation&nbsp;process.
              </p>
              <ul>
                <li>
                  Encourage idea submission - users can add ideas to your feedback board or upvote
                  existing&nbsp;ones
                </li>
                <li>
                  Involve your entire innovation team - add as many contributors as you like and
                  assign custom&nbsp;roles
                </li>
                <li>
                  Engage your community - interact with stakeholders on the ideas that matter
                  to&nbsp;them
                </li>
                <li>
                  Embrace continuous improvement - use feedback to make
                  incremental&nbsp;improvements
                </li>
              </ul>
            </div>
          </div>
          <Quotes title={<>A few of the companies who use Canny's idea&nbsp;software</>}>
            <Quote
              header={
                <>
                  CircleCI uses Canny's collaborative idea management systems to foster
                  open&nbsp;innovation
                </>
              }
              text={
                <>
                  [The product managers are] really able to go in and own that experience with their
                  users. It's definitely driven a lot of their product&nbsp;decisions.
                </>
              }
              person={
                <QuotePerson
                  name="Liya Ai"
                  image={Lia}
                  title={<>Product Operations Manager,&nbsp;CircleCI</>}
                />
              }
            />
            <Quote
              header={
                <>
                  Mercury relies on Canny's idea voting tools to evaluate ideas based on
                  user&nbsp;interest
                </>
              }
              text={
                <>
                  Voting has been powerful in [identifying] how big an issue is. Sometimes [it
                  seems] like a great idea. But, maybe like there's only a few people that feel
                  that&nbsp;way.
                </>
              }
              person={
                <QuotePerson
                  name="Ida Ström"
                  image={Ida}
                  title={<>Senior Product Designer,&nbsp;Mercury</>}
                />
              }
            />
            <Quote
              header={
                <>Canny helps Axios drive innovation and identify their most promising&nbsp;ideas</>
              }
              text={
                <>
                  Canny made the most important and urgent user feedback stand out, so we could
                  innovate our product and deliver an even better experience for our&nbsp;audience.
                </>
              }
              person={
                <QuotePerson
                  name="Marcus Moretti"
                  image={Marcus}
                  title={<>Product Development Lead,&nbsp;Axios</>}
                />
              }
            />
          </Quotes>
          <LandingFeaturesCTA />
          <LandingFooterCTA style="solid" expanded={true} />
          <FAQs
            faqs={[
              {
                question: 'How much does it cost?',
                answer:
                  '<p>You can get a free account to use our idea management tool as you get started. For teams with a more complex idea management process, we offer several plans on our <a href="https://canny.io/pricing" target="_blank" rel="noopener">pricing</a> page. They offer powerful features and integrations with other tools – check them out or <a href="https://canny.io/request-demo" target="_blank" rel="noopener">talk to our team.</a></p>',
                html: (
                  <p>
                    You can get a free account to use our idea management tool as you get started.
                    For teams with a more complex idea management process, we offer several plans on
                    our{' '}
                    <a href="https://canny.io/pricing" target="_blank" rel="noopener">
                      pricing
                    </a>{' '}
                    page. They offer powerful features and integrations with other tools – check
                    them out or{' '}
                    <a href="https://canny.io/request-demo" target="_blank" rel="noopener">
                      talk to our team.
                    </a>
                  </p>
                ),
              },
              {
                question: 'How do I collect ideas using Canny?',
                answer:
                  "<p>Lots of ways! Once you've set up your idea management platform in Canny, you can invite people to visit and add their ideas. You can also add ideas on behalf of your users — either directly or through one of our communication tool integrations. Think of it as a central idea box to manage ideas from all sources!</p>",
                html: (
                  <p>
                    Lots of ways! Once you've set up your idea management platform in Canny, you can
                    invite people to visit and add their ideas. You can also add ideas on behalf of
                    your users — either directly or through one of our communication tool
                    integrations. Think of it as a central idea box to manage ideas from all
                    sources!
                  </p>
                ),
              },
              {
                question: 'What integrations does Canny offer?',
                answer:
                  '<p>We have integrations with most popular tools for communication, customer success, product development, project management, and more. If it supports ideation and innovation management, we likely connect to it!</p>',
                html: (
                  <p>
                    We have integrations with most popular tools for communication, customer
                    success, product development, project management, and more. If it supports
                    ideation and innovation management, we likely connect to it!
                  </p>
                ),
              },
              {
                question: 'Can Canny help my innovation process?',
                answer:
                  "<p>Your users' feedback is a great way to drive ideation and innovation. You can capture that feedback in a new idea post and invite other users to discuss it and vote for it. This helps you discover new opportunities that your competitors haven't thought of. Brainstorming, mind maps, and other innovation activities are also valuable. But nothing beats learning from your users! It's the most insightful innovation effort you can do.</p><p>You can also create private boards to let your team help with idea generation. These are only visible to your team. This gives your team a way to brainstorm, discuss, and evaluate new ideas. It's not just a great tool for ideation — it also boosts employee engagement.</p>",
                html: (
                  <>
                    <p>
                      Your users' feedback is a great way to drive ideation and innovation. You can
                      capture that feedback in a new idea post and invite other users to discuss it
                      and vote for it. This helps you discover new opportunities that your
                      competitors haven't thought of. Brainstorming, mind maps, and other innovation
                      activities are also valuable. But nothing beats learning from your users! It's
                      the most insightful innovation effort you can do.
                    </p>
                    <p>
                      You can also create private boards to let your team help with idea generation.
                      These are only visible to your team. This gives your team a way to brainstorm,
                      discuss, and evaluate new ideas. It's not just a great tool for ideation — it
                      also boosts employee engagement.
                    </p>
                  </>
                ),
              },
              {
                question: 'How can you help me prioritize ideas?',
                answer:
                  '<p>Our prioritization tools let you decide which ideas are most important to your business. You can create your own prioritization formula that scores your ideas based on the criteria you set. Common factors are vote count, effort required, and associated revenue. This makes managing ideas simple, letting you focus on the highest scoring ideas first. It puts idea ranking on autopilot. </p>',
                html: (
                  <p>
                    Our prioritization tools let you decide which ideas are most important to your
                    business. You can create your own prioritization formula that scores your ideas
                    based on the criteria you set. Common factors are vote count, effort required,
                    and associated revenue. This makes managing ideas simple, letting you focus on
                    the highest scoring ideas first. It puts idea ranking on autopilot.
                  </p>
                ),
              },
            ]}
          />
        </div>
      </div>
    );
  }
}

export default compose(withContexts({ trackEvent: TrackEventContext }))(LandingIdeaManagement);
